import React, { useState, useRef, useEffect } from 'react';
import { aiApiFetch } from '../utils/apiFetch';
import CustomMarkdownRenderer from '../components/CustomMarkdownRenderer.js';
import HybridEvaluationRenderer from '../components/HybridEvaluationRenderer';
import mammoth from 'mammoth';
import Speedometer from 'react-d3-speedometer';
import '../styles/ResumeScan.css';
import { FiUpload, FiFileText, FiCheckCircle, FiAlertTriangle, FiTrash2, FiSearch } from 'react-icons/fi';

// Import pdfjs but handle the worker differently
import * as pdfjs from 'pdfjs-dist/webpack';

function ResumeScan() {

  useEffect(() => {
  try {
    const workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
    console.log("PDF.js worker set up successfully");
  } catch (error) {
    console.error("Error setting up PDF.js worker:", error);
  }
}, []);

  const [jdFile, setJdFile] = useState(null);
  const [candidateFile, setCandidateFile] = useState(null);
  const [jdContent, setJdContent] = useState('');
  const [candidateContent, setCandidateContent] = useState('');
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [shortlistingProbability, setShortlistingProbability] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isDraggingJd, setIsDraggingJd] = useState(false);
  const [isDraggingResume, setIsDraggingResume] = useState(false);

  const jdInputRef = useRef(null);
  const candidateInputRef = useRef(null);
  // Updated to include PDF files
  const validFileTypes = [
    'text/plain', 
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf'
  ];
  
  // File extensions for display purposes
  const supportedFormats = '.txt, .docx, .pdf';

  // Get color based on performance score
  const getScoreColor = (score) => {
    if (score >= 75) return "#4CAF50"; // Green for high scores
    if (score >= 50) return "#FFC107"; // Amber for medium scores
    return "#FF5252"; // Red for low scores
  };

  // Get recommendation text based on score
  const getRecommendationText = (score) => {
    if (score >= 75) return "Strong match - Candidate highly aligned with job requirements";
    if (score >= 50) return "Potential match - Consider additional assessment";
    return "Low alignment - May not be suitable for this role";
  };

  // Function to extract text from PDF file
  const extractTextFromPDF = async (file) => {
    try {
      // Check file size before processing
      if (file.size > 20 * 1024 * 1024) { // 20MB limit for PDF processing
        throw new Error('PDF file is too large to process.');
      }
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        // Use a better text extraction approach that respects document layout
        const textContent = await page.getTextContent({
          normalizeWhitespace: true,
          disableCombineTextItems: false
        });
        
        // Group text by vertical position to maintain paragraph structure
        const textItems = textContent.items;
        const textByY = {};
        
        textItems.forEach(item => {
          const y = Math.round(item.transform[5]); // Vertical position
          if (!textByY[y]) {
            textByY[y] = [];
          }
          textByY[y].push(item.str);
        });
        
        // Sort by vertical position (top to bottom)
        const sortedYs = Object.keys(textByY).sort((a, b) => b - a);
        
        // Build text with proper layout
        for (const y of sortedYs) {
          fullText += textByY[y].join(' ') + '\n';
        }
        
        fullText += '\n\n'; // Page separator
      }
      
      return fullText;
    } catch (err) {
      console.error('Error extracting text from PDF:', err);
      throw new Error('Failed to extract text from PDF file');
    }
  };

const MAX_FILE_SIZE_MB = 10; // 10MB max file size

const validateFileSize = (file) => {
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > MAX_FILE_SIZE_MB) {
    setErrorMessage(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please upload a smaller file.`);
    return false;
  }
  return true;
};

  // Updated file handler for JD
  const handleJdFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validFileTypes.includes(file.type)) {
      setErrorMessage(`Please upload a file in ${supportedFormats} format.`);
      setJdFile(null);
      setJdContent('');
      return;
    }

    if (!validateFileSize(file)) {
    return;
  }

    setErrorMessage('');
    setJdFile(file);

    try {
      let content = '';
      // Process based on file type
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        // DOCX file
        const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
        content = result.value;
      } else if (file.type === 'application/pdf') {
        // PDF file
        content = await extractTextFromPDF(file);
      } else {
        // TXT file or other text-based formats
        content = await readFileAsText(file);
      }
      
      setJdContent(content);
      if (currentStep === 1) setCurrentStep(2);
    } catch (err) {
      console.error("Error processing file:", err);
      setErrorMessage(`Failed to process job description file: ${err.message}`);
    }
  };

  // Updated file handler for resume
  const handleCandidateFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    if (!validFileTypes.includes(file.type)) {
      setErrorMessage(`Please upload a file in ${supportedFormats} format.`);
      setCandidateFile(null);
      setCandidateContent('');
      return;
    }
  
    if (!validateFileSize(file)) {
      return;
    }
  
    setErrorMessage('');
    setCandidateFile(file);
  
    try {
      let content = '';
      // Process based on file type
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        // DOCX file
        const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
        content = result.value;
      } else if (file.type === 'application/pdf') {
        // PDF file
        content = await extractTextFromPDF(file);
      } else {
        // TXT file or other text-based formats
        content = await readFileAsText(file);
      }
      
      setCandidateContent(content);
      if (currentStep === 2) setCurrentStep(3);
    } catch (err) {
      console.error("Error processing file:", err);
      setErrorMessage(`Failed to process resume file: ${err.message}`);
    }
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  // Updated drag and drop handler
  const handleDragOver = (e, type) => {
    e.preventDefault();
    if (type === 'jd') setIsDraggingJd(true);
    else setIsDraggingResume(true);
  };

  const handleDragLeave = (type) => {
    if (type === 'jd') setIsDraggingJd(false);
    else setIsDraggingResume(false);
  };

  // Updated drop handler with PDF support
  const handleDrop = async (e, type) => {
    e.preventDefault();
    if (type === 'jd') setIsDraggingJd(false);
    else setIsDraggingResume(false);
  
    const file = e.dataTransfer.files[0];
    if (!file) return;
  
    if (!validFileTypes.includes(file.type)) {
      setErrorMessage(`Please upload a ${type === 'jd' ? 'job description' : 'resume'} in ${supportedFormats} format.`);
      return;
    }
  
    if (!validateFileSize(file)) {
      return;
    }
  
    setErrorMessage('');
  
    if (type === 'jd') {
      setJdFile(file);
      
      try {
        // Browser compatibility check for DataTransfer API
        if (typeof DataTransfer !== 'undefined') {
          // Create a DataTransfer object to set files property
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          if (jdInputRef.current) {
            jdInputRef.current.files = dataTransfer.files;
          }
        }
  
        let content = '';
        if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
          const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
          content = result.value;
        } else if (file.type === 'application/pdf') {
          content = await extractTextFromPDF(file);
        } else {
          content = await readFileAsText(file);
        }
        
        setJdContent(content);
        if (currentStep === 1) setCurrentStep(2);
      } catch (err) {
        console.error("Error processing file:", err);
        setErrorMessage(`Failed to process job description file: ${err.message}`);
      }
    } else {
      setCandidateFile(file);
      
      try {
        // Browser compatibility check for DataTransfer API
        if (typeof DataTransfer !== 'undefined') {
          // Create a DataTransfer object to set files property
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          if (candidateInputRef.current) {
            candidateInputRef.current.files = dataTransfer.files;
          }
        }
  
        let content = '';
        if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
          const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
          content = result.value;
        } else if (file.type === 'application/pdf') {
          content = await extractTextFromPDF(file);
        } else {
          content = await readFileAsText(file);
        }
        
        setCandidateContent(content);
        if (currentStep === 2) setCurrentStep(3);
      } catch (err) {
        console.error("Error processing file:", err);
        setErrorMessage(`Failed to process resume file: ${err.message}`);
      }
    }
  };

  // The rest of your code remains largely unchanged
  const handleAssessClick = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    
    // Clear previous results to avoid showing stale data
    setAssessmentResult(null);
    setShortlistingProbability(null);
  
    try {
      console.log("Sending request with data:", { 
        resumeText: candidateContent.substring(0, 100) + "...", 
        jobDescriptionText: jdContent.substring(0, 100) + "..." 
      });
      
      const response = await aiApiFetch('/evaluate', {
        method: 'POST',
        body: JSON.stringify({ resumeText: candidateContent, jobDescriptionText: jdContent }),
      });
  
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || response.statusText}`);
        } catch (jsonError) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
  
      const data = await response.json();
      console.log("Received data structure:", Object.keys(data));
      
      if (data.error) {
        throw new Error(data.error);
      }
  
      if (!data.result) {
        throw new Error("API returned empty result");
      }
  
      // Store the entire result
      const resultText = data.result;
      console.log("Result data type:", typeof resultText);
      
      // First ensure we have assessment result before setting other states
      setAssessmentResult(resultText);
      
      // Try to parse the result to extract the score
      try {
        let score = 0;
        let parsedResult;
        
        // First try to parse as JSON
        if (typeof resultText === 'string') {
          try {
            parsedResult = JSON.parse(resultText);
            console.log("Successfully parsed result as JSON");
          } catch (parseErr) {
            console.warn("Result is not valid JSON, treating as plain text");
          }
        } else {
          parsedResult = resultText;
        }
        
        // Extract score from structured data if available
        if (parsedResult && parsedResult.structured && parsedResult.structured.overallScore !== undefined) {
          score = parsedResult.structured.overallScore;
          console.log("Found structured score:", score);
        }
        // Extract from markdown if available
        else if (parsedResult && parsedResult.markdown) {
          const scoreMatch = parsedResult.markdown.match(/Overall Alignment Score:\s*(\d+(\.\d+)?)\s*%/i);
          if (scoreMatch) {
            score = parseFloat(scoreMatch[1]);
            console.log("Extracted score from markdown:", score);
          }
        }
        // Fall back to direct regex on the raw result
        else {
          const content = typeof resultText === 'string' ? resultText : JSON.stringify(resultText);
          const scoreMatch = content.match(/Overall Alignment Score:\s*(\d+(\.\d+)?)\s*%/i);
          if (scoreMatch) {
            score = parseFloat(scoreMatch[1]);
            console.log("Extracted score from raw text:", score);
          } else {
            console.warn("Could not extract score, using default");
            score = 50; // Default if we can't extract
          }
        }
        
        // Ensure score is a valid number
        score = isNaN(score) ? 50 : score;
        console.log("Final score to display:", score);
        
        // Update the score state
        setShortlistingProbability(score);
      } catch (parseError) {
        console.error("Error extracting score:", parseError);
        setShortlistingProbability(50); // Default value
      }
  
      if (currentStep === 3) {
        setCurrentStep(4);
      }
    } catch (error) {
      console.error("Error in assessment:", error);
      setError(error.message);
      // Even with error, move to step 4 to show the error
      if (currentStep === 3) {
        setCurrentStep(4);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    // Same implementation as before...
    setJdFile(null);
    setCandidateFile(null);
    setJdContent('');
    setCandidateContent('');
    setErrorMessage('');
    setAssessmentResult(null);
    setShortlistingProbability(null);
    setCurrentStep(1);

    if (jdInputRef.current) {
      jdInputRef.current.value = null;
    }

    if (candidateInputRef.current) {
      candidateInputRef.current.value = null;
    }

    if (jdContent && candidateContent) {
      aiApiFetch('/clearcache', {
        method: 'POST',
        body: JSON.stringify({ 
          resumeText: candidateContent, 
          jobDescriptionText: jdContent 
        }),
      })
        .then(response => response.json())
        .then(data => {
          if (data.cleared) {
            console.log('Cache cleared successfully');
          }
        })
        .catch(err => {
          console.error('Failed to clear cache:', err);
        });
    }
  };

  const removeFile = (type) => {
    // Same implementation as before...
    if (type === 'jd') {
      setJdFile(null);
      setJdContent('');
      if (jdInputRef.current) jdInputRef.current.value = null;
    } else {
      setCandidateFile(null);
      setCandidateContent('');
      if (candidateInputRef.current) candidateInputRef.current.value = null;
    }
  };

  const isAssessButtonEnabled = jdContent && candidateContent;

  // Update the JSX to reflect PDF support
  return (
    <div className="resume-scan-container">
      {/* Progress Tracker */}
      <div className="progress-tracker">
        {/* Same implementation as before... */}
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-indicator">1</div>
          <div className="step-label">Upload Job Description</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-indicator">2</div>
          <div className="step-label">Upload Resume</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
          <div className="step-indicator">3</div>
          <div className="step-label">Generate Analysis</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 4 ? 'active' : ''}`}>
          <div className="step-indicator">4</div>
          <div className="step-label">View Results</div>
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="error-notification">
          <FiAlertTriangle size={20} />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="resume-scan-content">
        <div className="upload-section-container">
          {/* Single Card with Both Upload Areas */}
          <div className="upload-card">
            <div className="card-header">
              <h3>File Uploads</h3>
            </div>
            <div className="card-body">
              {/* Two-Column Layout */}
              <div className="upload-areas-container">
                {/* JD Upload Area - Updated to show PDF support */}
                <div className="upload-area">
                  <h4 className="upload-area-title">Upload Job Description</h4>
                  {!jdFile ? (
                    <div
                      className={`file-drop-area ${isDraggingJd ? 'dragging' : ''}`}
                      onDragOver={(e) => handleDragOver(e, 'jd')}
                      onDragLeave={() => handleDragLeave('jd')}
                      onDrop={(e) => handleDrop(e, 'jd')}
                      onClick={() => jdInputRef.current.click()}
                    >
                      <div className="upload-icon">
                        <FiUpload size={28} color="#1976d2" />
                      </div>
                      <div className="upload-text">
                        <p className="upload-subtitle">
                          Drop your file here, or <span className="browse-text">browse</span>
                        </p>
                        <p className="file-types">Supported formats: {supportedFormats}</p>
                      </div>
                      <input
                        ref={jdInputRef}
                        id="jd-upload"
                        type="file"
                        accept=".txt,.docx,.pdf"
                        onChange={handleJdFileChange}
                        className="file-input"
                      />
                    </div>
                  ) : (
                    <div className="file-preview">
                      <div className="file-info">
                        <div className="file-icon">
                          <FiFileText size={24} color="#1976d2" />
                        </div>
                        <div className="file-details">
                          <p className="file-name">{jdFile.name}</p>
                          <p className="file-size">{(jdFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <div className="file-actions">
                        <button
                          className="icon-button preview-btn"
                          title="Preview content"
                          onClick={() => alert(jdContent.substring(0, 500) + "...")}
                        >
                          <FiSearch size={18} />
                        </button>
                        <button
                          className="icon-button remove-btn"
                          title="Remove file"
                          onClick={() => removeFile('jd')}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Resume Upload Area - Updated to show PDF support */}
                <div className="upload-area">
                  <h4 className="upload-area-title">Upload Resume</h4>
                  {!candidateFile ? (
                    <div
                      className={`file-drop-area ${isDraggingResume ? 'dragging' : ''}`}
                      onDragOver={(e) => handleDragOver(e, 'resume')}
                      onDragLeave={() => handleDragLeave('resume')}
                      onDrop={(e) => handleDrop(e, 'resume')}
                      onClick={() => candidateInputRef.current.click()}
                    >
                      <div className="upload-icon">
                        <FiUpload size={28} color="#1976d2" />
                      </div>
                      <div className="upload-text">
                        <p className="upload-subtitle">
                          Drop your file here, or <span className="browse-text">browse</span>
                        </p>
                        <p className="file-types">Supported formats: {supportedFormats}</p>
                      </div>
                      <input
                        ref={candidateInputRef}
                        id="candidate-upload"
                        type="file"
                        accept=".txt,.docx,.pdf"
                        onChange={handleCandidateFileChange}
                        className="file-input"
                      />
                    </div>
                  ) : (
                    <div className="file-preview">
                      <div className="file-info">
                        <div className="file-icon">
                          <FiFileText size={24} color="#1976d2" />
                        </div>
                        <div className="file-details">
                          <p className="file-name">{candidateFile.name}</p>
                          <p className="file-size">{(candidateFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <div className="file-actions">
                        <button
                          className="icon-button preview-btn"
                          title="Preview content"
                          onClick={() => alert(candidateContent.substring(0, 500) + "...")}
                        >
                          <FiSearch size={18} />
                        </button>
                        <button
                          className="icon-button remove-btn"
                          title="Remove file"
                          onClick={() => removeFile('resume')}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {/* Rest remains the same */}
            <button
              className={`btn btn-primary ${!isAssessButtonEnabled ? 'disabled' : ''}`}
              onClick={handleAssessClick}
              disabled={!isAssessButtonEnabled || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Analyzing...
                </>
              ) : (
                <>Analyze Resume Alignment</>
              )}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>

        {/* Right section with gauge and results */}
        <div className="results-section">
          {/* Gauge Container */}
          {/* Rest remains the same */}
          <div className="gauge-container">
            <h3 className="gauge-title">Resume Alignment Score</h3>

            <div className="gauge-visualization">
              <Speedometer
                maxValue={100}
                value={shortlistingProbability || 0}
                needleColor="#333333"
                startColor="#FF5252"
                endColor="#4CAF50"
                segments={3}
                segmentColors={["#FF5252", "#FFC107", "#4CAF50"]}
                currentValueText={`${shortlistingProbability || 0}%`}
                textColor="#212121"
                valueTextFontSize="20px"
                needleTransitionDuration={500}
                ringWidth={25}
                height={230}
                width={300}
                fluidWidth={false}
              />
            </div>

            {shortlistingProbability && (
              <div className={`alignment-recommendation ${
                shortlistingProbability >= 75 ? 'high' :
                shortlistingProbability >= 50 ? 'medium' : 'low'
              }`}>
                <div className="recommendation-icon">
                  {shortlistingProbability >= 0 ?
                    <FiCheckCircle size={18} /> :
                    <FiAlertTriangle size={18} />
                  }
                </div>
                <div className="recommendation-text">
                  {getRecommendationText(shortlistingProbability)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assessment Results */}
      {/* Rest remains the same */}
      {(isLoading || assessmentResult || error) && (
        <div className="assessment-results-container">
          <h2 className="results-title">Detailed Assessment Results</h2>

          {isLoading ? (
            <div className="loader-container">
              <div className="loader"></div>
              <div className="loader-text">Analyzing resume alignment...</div>
            </div>
          ) : error ? (
            <div className="error-container">
              <FiAlertTriangle size={28} />
              <h3>Error</h3>
              <p>{error}</p>
            </div>
          ) : assessmentResult ? (
            <div className="evaluation-result">
              <HybridEvaluationRenderer evaluationResult={assessmentResult} />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ResumeScan;